/**
 * KAN-826: requisites of a company (name, ИНН, ОГРН, address, city, region,
 * phones, e-mails, website) with "Сохранить" and the server field errors
 * under the fields. State and save live in useClientCompanyEditor; without
 * edit access the fields are disabled and there is no save.
 */
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StringListField } from './StringListField';
import type { ClientCompanyEditor, CompanyTextField } from '@/hooks/useClientCompanies';

interface ClientCompanyRequisitesCardProps {
  editor: ClientCompanyEditor;
}

const TEXT_FIELDS: ReadonlyArray<{ field: CompanyTextField; label: string; wide?: boolean }> = [
  { field: 'name', label: 'Название', wide: true },
  { field: 'inn', label: 'ИНН' },
  { field: 'ogrn', label: 'ОГРН' },
  { field: 'address', label: 'Адрес', wide: true },
  { field: 'city', label: 'Город' },
  { field: 'region', label: 'Регион' },
  { field: 'website', label: 'Сайт', wide: true },
];

export function ClientCompanyRequisitesCard({ editor }: ClientCompanyRequisitesCardProps) {
  const { form, errors, canEdit, isSaving } = editor;
  if (!form) return null;
  const readOnly = !canEdit;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>Реквизиты</CardTitle>
        {canEdit && (
          <Button type="button" size="sm" onClick={editor.save} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Сохранить
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (canEdit) editor.save();
          }}
        >
          {TEXT_FIELDS.map(({ field, label, wide }) => {
            const id = `company-${field}`;
            const error = errors[field];
            return (
              <div key={field} className={`space-y-2${wide ? ' md:col-span-2' : ''}`}>
                <Label htmlFor={id}>{label}</Label>
                <Input
                  id={id}
                  value={form[field]}
                  disabled={readOnly}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? `${id}-error` : undefined}
                  onChange={(e) => editor.setField(field, e.target.value)}
                />
                {error && (
                  <p id={`${id}-error`} data-testid={`field-error-${field}`} className="text-sm text-red-600">
                    {error}
                  </p>
                )}
              </div>
            );
          })}
          <StringListField
            id="phones"
            label="Телефоны"
            itemLabel="Телефон"
            addLabel="Добавить телефон"
            type="tel"
            values={form.phones}
            readOnly={readOnly}
            error={errors.phones}
            onChange={(index, value) => editor.setListItem('phones', index, value)}
            onAdd={() => editor.addListItem('phones')}
            onRemove={(index) => editor.removeListItem('phones', index)}
          />
          <StringListField
            id="emails"
            label="Почты"
            itemLabel="Почта"
            addLabel="Добавить почту"
            type="email"
            values={form.emails}
            readOnly={readOnly}
            error={errors.emails}
            onChange={(index, value) => editor.setListItem('emails', index, value)}
            onAdd={() => editor.addListItem('emails')}
            onRemove={(index) => editor.removeListItem('emails', index)}
          />
          {/* Enter in a field saves the form. */}
          <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
        </form>
      </CardContent>
    </Card>
  );
}
